import scenariosDoc from "../../../data/tables/raid/sim-scenarios.json";
import { raidCatalog, setRaidAiSave } from "../../../data/raid-catalog";
import { effectiveThreat, noteHit, addThreat, runSimSteps } from "../../../sim";
import { formatDisplay, formatInt } from "../../../view/display-format";
import { isBossDormant } from "../raid-assault";
import { collectRaidDangerZones, unitInDangerZones } from "../raid-threat";
import {
  createBattleState,
  tickRaid,
} from "../raid-rules";
import type { RaidBattleState, RaidUnit } from "../raid-types";
import { enemyIsCombatRelevant } from "../raid-enemy";
import { applySimSetup } from "./sim-setup";
import {
  buildPlayMapStage,
  clearSimMapOverride,
  getSimStage,
  resolveSimStageForRun,
  setSimMapOverride,
} from "./sim-stage";
import type {
  SimEditPatch,
  SimMetrics,
  SimPrimaryId,
  SimReport,
  SimScenarioDef,
  SimScenarioId,
  SimSetupRuntime,
  SimStageDef,
} from "./sim-lab-types";

const DT = 1 / 60;

/** Runtime absolute keys (insight is virtual — expands before setRaidAiSave). */
const RUNTIME_PRIMARIES = [
  "grit",
  "measure",
  "selfProtect",
  "bond",
  "instinct",
] as const;

const scenarios = scenariosDoc.scenarios as SimScenarioDef[];

function clampPrimary(n: number): number {
  return Math.max(0, Math.min(100, n));
}

/**
 * Expand sell-sheet `insight` → measure+selfProtect (judgmentToLegacy).
 * Strips insight so RaidAiSave stays grit/measure/selfProtect/bond/instinct.
 */
export function expandInsightInAbsoluteRow(
  row: Record<string, number | undefined>,
): Record<string, number> {
  const next: Record<string, number> = {};
  for (const [k, v] of Object.entries(row)) {
    if (k === "insight" || v == null || !Number.isFinite(v)) continue;
    next[k] = clampPrimary(v);
  }
  const insight = row.insight;
  if (insight != null && Number.isFinite(insight)) {
    const v = clampPrimary(insight);
    next.measure = v;
    next.selfProtect = v;
  }
  return next;
}

/** focusPrimaries may list `insight` — unlocks both measure and selfProtect. */
export function expandEditablePrimaries(
  keys: readonly SimPrimaryId[],
): ReadonlySet<SimPrimaryId> {
  const out = new Set<SimPrimaryId>();
  for (const k of keys) {
    if (k === "insight") {
      out.add("insight");
      out.add("measure");
      out.add("selfProtect");
    } else {
      out.add(k);
    }
  }
  return out;
}

export function listSimScenarios(): readonly SimScenarioDef[] {
  return scenarios;
}

/** Sell-sheet demos (same as listSimScenarios — retired labs removed). */
export function listSellSimScenarios(): readonly SimScenarioDef[] {
  return scenarios.filter((s) => (s.sellStatus ?? "active") === "active");
}

export function getSimScenario(id: SimScenarioId): SimScenarioDef {
  const row = scenarios.find((s) => s.id === id);
  if (!row) throw new Error(`unknown sim scenario: ${id}`);
  return row;
}

/** Pitch / demo A|B banks when scenario.pitchBanks is authored. */
export function getScenarioPitchBanks(id: SimScenarioId) {
  return getSimScenario(id).pitchBanks ?? null;
}

/** Axes the operator may edit for this scenario (empty = Role / all locked). */
export function editablePrimaries(
  scenario: SimScenarioDef,
): readonly SimPrimaryId[] {
  return scenario.focusPrimaries;
}

/**
 * Force non-editable primaries to absolute 50 so single-axis runs stay pure
 * (HUD + headless share this).
 */
export function mergeLockedPrimaries(
  scenario: SimScenarioDef,
  patch: SimEditPatch,
): SimEditPatch {
  const focus = scenario.focusDefId;
  const editable = expandEditablePrimaries(scenario.focusPrimaries);
  const prevRaw = patch.absolute?.[focus] ?? {};
  const prev = expandInsightInAbsoluteRow(
    prevRaw as Record<string, number | undefined>,
  );
  const next: Record<string, number> = {};
  for (const key of RUNTIME_PRIMARIES) {
    if (
      editable.has(key) &&
      prev[key] != null &&
      Number.isFinite(prev[key])
    ) {
      next[key] = clampPrimary(prev[key]!);
    } else {
      // Non-focus OR editable-but-unspecified → 50 (axis purity).
      next[key] = 50;
    }
  }
  return {
    ...patch,
    absolute: {
      ...patch.absolute,
      [focus]: next,
    },
  };
}

/**
 * Copy focus absolute onto every party defId when scenario.absoluteParty.
 * Party-formation labs (wave screen) need shared bond, not focus-only.
 */
export function expandAbsoluteToParty(
  scenario: SimScenarioDef,
  patch: SimEditPatch,
): SimEditPatch {
  if (!scenario.absoluteParty) return patch;
  const focusAbs = patch.absolute?.[scenario.focusDefId];
  if (!focusAbs) return patch;
  const party = scenario.party ?? raidCatalog.roster.defaultParty;
  const absolute: NonNullable<SimEditPatch["absolute"]> = {
    ...patch.absolute,
  };
  for (const defId of party) {
    absolute[defId] = { ...absolute[defId], ...focusAbs };
  }
  return { ...patch, absolute };
}

/** Expand insight on every absolute row before setRaidAiSave. */
export function expandInsightInPatch(patch: SimEditPatch): SimEditPatch {
  if (!patch.absolute) return patch;
  const absolute: NonNullable<SimEditPatch["absolute"]> = {};
  for (const [defId, row] of Object.entries(patch.absolute)) {
    absolute[defId] = expandInsightInAbsoluteRow(
      row as Record<string, number | undefined>,
    );
  }
  return { ...patch, absolute };
}

type SimMetricsAcc = {
  timeInDangerZone: number;
  surviveTriggers: number;
  timeToSurviveSec: number | null;
  targetSwitches: number;
  timeToFirstSwitchSec: number | null;
  distanceSum: number;
  distanceSamples: number;
  allyDistSum: number;
  allyDistSamples: number;
  dangerEnterT0: number | null;
  dodgeReactionLagSec: number | null;
  prevTargetId: number;
  pulseAcc: number;
  spawnPulseAcc: number;
  chaseThreatAcc: number;
  rangedForwardExposureSec: number;
  partySpreadSum: number;
  partySpreadSamples: number;
  centroidCenterSum: number;
  centroidCenterSamples: number;
  partyStartAlive: number;
};

/** Live / headless shared run context. */
export type SimRunContext = {
  scenarioId: SimScenarioId;
  scenario: SimScenarioDef;
  stage: SimStageDef;
  patch: SimEditPatch;
  state: RaidBattleState;
  focus: RaidUnit;
  runtime: SimSetupRuntime;
  acc: SimMetricsAcc;
  finished: boolean;
};

function partyFor(scenario: SimScenarioDef): readonly string[] {
  return scenario.party ?? raidCatalog.roster.defaultParty;
}

function findFocus(state: RaidBattleState, defId: string): RaidUnit {
  const u = state.units.find((x) => x.defId === defId && x.team === 0);
  if (!u) throw new Error(`focus unit ${defId} not in party`);
  return u;
}

function nearestThreatDist(focus: RaidUnit, state: RaidBattleState): number {
  let nearest = Infinity;
  for (const h of state.units) {
    if (h.team !== 1 || h.hp <= 0) continue;
    if (!enemyIsCombatRelevant(h)) continue;
    const d = Math.hypot(focus.x - h.x, focus.y - h.y);
    if (d < nearest) nearest = d;
  }
  if (!isBossDormant(state)) {
    const db = Math.hypot(focus.x - state.boss.x, focus.y - state.boss.y);
    if (db < nearest) nearest = db;
  }
  return nearest;
}

function allyDist(
  focus: RaidUnit,
  state: RaidBattleState,
  allyId: number | undefined,
): number | null {
  if (allyId == null) return null;
  const ally = state.units.find((u) => u.id === allyId && u.hp > 0);
  if (!ally) return null;
  return Math.hypot(focus.x - ally.x, focus.y - ally.y);
}

function tickPulse(ctx: SimRunContext, dt: number): void {
  const pulse = ctx.runtime.pulseHit;
  if (!pulse) return;
  ctx.acc.pulseAcc += dt;
  while (ctx.acc.pulseAcc >= pulse.everySec) {
    ctx.acc.pulseAcc -= pulse.everySec;
    noteHit(ctx.focus.hit, pulse.amount, pulse.attackerId, ctx.state.time);
  }
}

function pinUnitAt(
  u: RaidUnit | undefined,
  pin: { x: number; y: number } | undefined,
): void {
  if (!u || u.hp <= 0 || !pin) return;
  u.x = pin.x;
  u.y = pin.y;
  u.vx = 0;
  u.vy = 0;
  u.intentVx = 0;
  u.intentVy = 0;
  u.speed = 0;
}

/** Pin park ally + walk chase peel toward focus. */
function tickSimRuntime(ctx: SimRunContext, dt: number): void {
  if (ctx.runtime.pinBoss) {
    const boss = ctx.state.boss;
    const pin = ctx.runtime.pinBoss;
    boss.speed = 0;
    boss.x = pin.x;
    boss.y = pin.y;
    boss.vx = 0;
    boss.vy = 0;
    boss.intentVx = 0;
    boss.intentVy = 0;
  }
  if (ctx.runtime.pinAlly && ctx.runtime.allyId != null) {
    pinUnitAt(
      ctx.state.units.find((u) => u.id === ctx.runtime.allyId),
      ctx.runtime.pinAlly,
    );
  }
  if (ctx.runtime.pinPrimary && ctx.runtime.primaryId != null) {
    pinUnitAt(
      ctx.state.units.find((u) => u.id === ctx.runtime.primaryId),
      ctx.runtime.pinPrimary,
    );
  }

  const chaseId = ctx.runtime.peelChaseId;
  if (chaseId == null) return;
  const peel = ctx.state.units.find((u) => u.id === chaseId && u.hp > 0);
  if (!peel) return;
  const focus = ctx.focus;
  const dist = Math.hypot(focus.x - peel.x, focus.y - peel.y) || 1;
  // Peel walks via team-1 AI only (no puppet double-chase).
  peel.aimX = focus.x;
  peel.aimY = focus.y;

  // Reinforcement threat: first AA enter → peel lead in continuum band
  // (~10–25). Beats bond100 thin sticky; mid delays; bond0 holds longest.
  // No continuous chaseThreat drip (harness honesty).
  const primaryId = ctx.runtime.primaryId;
  const reach = focus.attackRange + peel.radius;
  if (
    !ctx.runtime.peelAaThreatArmed &&
    primaryId != null &&
    dist < reach
  ) {
    ctx.runtime.peelAaThreatArmed = true;
    const priT = effectiveThreat(focus.aggro, primaryId, ctx.state.time);
    const peelT = effectiveThreat(focus.aggro, peel.id, ctx.state.time);
    const want = priT + 18;
    if (peelT < want) addThreat(focus.aggro, peel.id, want - peelT);
  }

  if (dist < focus.radius + peel.radius + 10) {
    ctx.acc.chaseThreatAcc += dt;
    if (ctx.acc.chaseThreatAcc >= 0.3) {
      ctx.acc.chaseThreatAcc = 0;
      noteHit(focus.hit, 8, peel.id, ctx.state.time);
    }
  }
}

function collectTickMetrics(ctx: SimRunContext, dt: number): void {
  const { state, focus, acc } = ctx;
  const enemies = state.units.filter((u) => u.team === 1);
  const zones = collectRaidDangerZones(state.boss, { enemies });
  const inDanger = unitInDangerZones(focus, zones);

  // Reaction clock starts when a telegraph is painted (인지), not only when
  // feet are already inside — feet-in instant dodge would otherwise zero lag.
  if (zones.length > 0 && acc.dangerEnterT0 == null) {
    acc.dangerEnterT0 = state.time;
  }
  if (inDanger) {
    acc.timeInDangerZone += dt;
  }

  if (
    acc.dodgeReactionLagSec == null &&
    focus.brain.select.activeId === "dodge" &&
    acc.dangerEnterT0 != null
  ) {
    acc.dodgeReactionLagSec = state.time - acc.dangerEnterT0;
  }

  if (
    focus.brain.select.activeId === "survive" ||
    focus.brain.select.activeId === "tank_critical_fallback"
  ) {
    acc.surviveTriggers += 1;
    if (acc.timeToSurviveSec == null) {
      acc.timeToSurviveSec = state.time;
    }
  }

  if (focus.brain.targetId !== acc.prevTargetId) {
    acc.targetSwitches += 1;
    if (acc.timeToFirstSwitchSec == null) {
      acc.timeToFirstSwitchSec = state.time;
    }
    acc.prevTargetId = focus.brain.targetId;
  }

  const dist = nearestThreatDist(focus, state);
  if (Number.isFinite(dist)) {
    acc.distanceSum += dist;
    acc.distanceSamples += 1;
  }

  const ad = allyDist(focus, state, ctx.runtime.allyId);
  if (ad != null) {
    acc.allyDistSum += ad;
    acc.allyDistSamples += 1;
  }

  const allies = state.units.filter((u) => u.team === 0 && u.hp > 0);
  if (allies.length >= 1) {
    let cx = 0;
    let cy = 0;
    for (const a of allies) {
      cx += a.x;
      cy += a.y;
    }
    cx /= allies.length;
    cy /= allies.length;
    const b = ctx.stage.playBounds;
    const midX = (b.xMin + b.xMax) * 0.5;
    const midY = (b.yMin + b.yMax) * 0.5;
    acc.centroidCenterSum += Math.hypot(cx - midX, cy - midY);
    acc.centroidCenterSamples += 1;

    if (allies.length >= 2) {
      let pairSum = 0;
      let pairs = 0;
      for (let i = 0; i < allies.length; i++) {
        for (let j = i + 1; j < allies.length; j++) {
          const a = allies[i]!;
          const bAlly = allies[j]!;
          pairSum += Math.hypot(a.x - bAlly.x, a.y - bAlly.y);
          pairs += 1;
        }
      }
      if (pairs > 0) {
        acc.partySpreadSum += pairSum / pairs;
        acc.partySpreadSamples += 1;
      }
    }

    const hostiles = enemies.filter((e) => enemyIsCombatRelevant(e));
    if (hostiles.length > 0) {
      let hx = 0;
      let hy = 0;
      for (const h of hostiles) {
        hx += h.x;
        hy += h.y;
      }
      hx /= hostiles.length;
      hy /= hostiles.length;
      let fx = hx - cx;
      let fy = hy - cy;
      const fl = Math.hypot(fx, fy) || 1;
      fx /= fl;
      fy /= fl;
      const tanks = allies.filter((a) => a.classId === "tank");
      const ranged = allies.filter(
        (a) => a.classId === "dps" || a.classId === "healer",
      );
      if (tanks.length > 0 && ranged.length > 0) {
        let tankAlong = 0;
        for (const t of tanks) {
          tankAlong += (t.x - cx) * fx + (t.y - cy) * fy;
        }
        tankAlong /= tanks.length;
        for (const r of ranged) {
          const along = (r.x - cx) * fx + (r.y - cy) * fy;
          if (along > tankAlong + 8) {
            acc.rangedForwardExposureSec += dt / ranged.length;
          }
        }
      }
    }
  }
}

function finalizeMetrics(
  scenario: SimScenarioDef,
  focus: RaidUnit,
  state: RaidBattleState,
  acc: SimMetricsAcc,
): SimMetrics {
  const partyUnits = state.units.filter((u) => u.team === 0 && u.hp > 0);
  const partyAlive = partyUnits.length;
  const partyDeaths = Math.max(0, acc.partyStartAlive - partyAlive);
  const partyHpRemaining = partyUnits.reduce((s, u) => s + u.hp, 0);
  return {
    hpRemaining: focus.hp,
    alive: focus.hp > 0 ? 1 : 0,
    partyAlive,
    partyDeaths,
    partyHpRemaining,
    timeInDangerZone: acc.timeInDangerZone,
    dodgeReactionLagSec: acc.dodgeReactionLagSec ?? scenario.durationSec,
    surviveTriggers: acc.surviveTriggers,
    timeToSurviveSec: acc.timeToSurviveSec ?? scenario.durationSec,
    targetSwitches: acc.targetSwitches,
    timeToFirstSwitchSec: acc.timeToFirstSwitchSec ?? scenario.durationSec,
    distanceToThreatMean:
      acc.distanceSamples > 0 ? acc.distanceSum / acc.distanceSamples : 0,
    distanceToAllyMean:
      acc.allyDistSamples > 0 ? acc.allyDistSum / acc.allyDistSamples : 0,
    rangedForwardExposureSec: acc.rangedForwardExposureSec,
    partySpreadMean:
      acc.partySpreadSamples > 0
        ? acc.partySpreadSum / acc.partySpreadSamples
        : 0,
    centroidCenterDist:
      acc.centroidCenterSamples > 0
        ? acc.centroidCenterSum / acc.centroidCenterSamples
        : 0,
  };
}

/** Short live caption token for the scenario's primary read metric. */
export function liveMetricToken(ctx: SimRunContext): string {
  const m = finalizeMetrics(ctx.scenario, ctx.focus, ctx.state, ctx.acc);
  const wanted = ctx.scenario.metrics;
  const order: (keyof SimMetrics)[] = [
    "partyDeaths",
    "partyAlive",
    "partyHpRemaining",
    "timeToSurviveSec",
    "surviveTriggers",
    "dodgeReactionLagSec",
    "timeInDangerZone",
    "distanceToThreatMean",
    "targetSwitches",
    "timeToFirstSwitchSec",
    "distanceToAllyMean",
    "rangedForwardExposureSec",
    "partySpreadMean",
    "centroidCenterDist",
  ];
  for (const key of order) {
    if (!wanted.includes(key)) continue;
    const v = m[key];
    if (v == null) continue;
    if (key === "partyDeaths") return `deaths×${formatInt(v)}`;
    if (key === "partyAlive") return `alive×${formatInt(v)}`;
    if (key === "partyHpRemaining") return `partyHP ${formatInt(v)}`;
    if (key === "timeToSurviveSec") return `tSurv ${formatDisplay(v, 1)}s`;
    if (key === "surviveTriggers") return `survive×${formatInt(v)}`;
    if (key === "targetSwitches") return `sw×${formatInt(v)}`;
    if (key === "timeToFirstSwitchSec") return `tSw ${formatDisplay(v, 1)}s`;
    if (key === "dodgeReactionLagSec") return `lag ${formatDisplay(v, 1)}s`;
    if (key === "timeInDangerZone") return `danger ${formatDisplay(v, 1)}s`;
    if (key === "distanceToThreatMean") return `threat ${formatInt(v)}`;
    if (key === "distanceToAllyMean") return `ally ${formatInt(v)}`;
    if (key === "rangedForwardExposureSec") {
      return `fwd ${formatDisplay(v, 1)}s`;
    }
    if (key === "partySpreadMean") return `spread ${formatInt(v)}`;
    if (key === "centroidCenterDist") return `center ${formatInt(v)}`;
  }
  return "";
}

/** Apply patch + stage override + create battle + declarative setup. */
export function prepareSimRun(
  id: SimScenarioId,
  patch: SimEditPatch = {},
): SimRunContext {
  const base = getSimScenario(id);
  const scenario: SimScenarioDef =
    patch.durationSec != null && Number.isFinite(patch.durationSec)
      ? { ...base, durationSec: Math.max(0.1, patch.durationSec) }
      : base;
  const usePlayMap = patch.usePlayMap === true;
  const stage = usePlayMap
    ? buildPlayMapStage()
    : resolveSimStageForRun(getSimStage(scenario.stageId));
  const {
    lockNonFocus,
    durationSec: _dur,
    usePlayMap: _play,
    rng,
    cosmeticRng,
    ...savePatch
  } = patch;
  const locked =
    lockNonFocus === true
      ? mergeLockedPrimaries(scenario, savePatch)
      : expandInsightInPatch(savePatch);
  const appliedPatch = expandInsightInPatch(
    expandAbsoluteToParty(scenario, locked),
  );
  setRaidAiSave(appliedPatch);
  setSimMapOverride(stage);
  const state = createBattleState(partyFor(scenario), { rng, cosmeticRng });
  const focus = findFocus(state, scenario.focusDefId);
  const preserveFormation =
    usePlayMap && scenario.setup.kind === "assault_wave";
  const runtime = applySimSetup(state, focus, stage, scenario.setup, {
    preserveFormation,
  });
  const partyStartAlive = state.units.filter(
    (u) => u.team === 0 && u.hp > 0,
  ).length;
  return {
    scenarioId: id,
    scenario,
    stage,
    patch: appliedPatch,
    state,
    focus,
    runtime,
    finished: false,
    acc: {
      timeInDangerZone: 0,
      surviveTriggers: 0,
      timeToSurviveSec: null,
      targetSwitches: 0,
      timeToFirstSwitchSec: null,
      distanceSum: 0,
      distanceSamples: 0,
      allyDistSum: 0,
      allyDistSamples: 0,
      dangerEnterT0: null,
      dodgeReactionLagSec: null,
      prevTargetId: focus.brain.targetId,
      pulseAcc: 0,
      spawnPulseAcc: 0,
      chaseThreatAcc: 0,
      rangedForwardExposureSec: 0,
      partySpreadSum: 0,
      partySpreadSamples: 0,
      centroidCenterSum: 0,
      centroidCenterSamples: 0,
      partyStartAlive,
    },
  };
}

/** One sim step. Returns true when duration elapsed or phase ended. */
export function stepSimRun(ctx: SimRunContext, dt: number): boolean {
  if (ctx.finished) return true;
  setRaidAiSave(ctx.patch);
  tickPulse(ctx, dt);
  tickSimRuntime(ctx, dt);
  tickRaid(ctx.state, dt);
  // Soft-sep / boss clamp may nudge parks — re-pin after physics.
  if (ctx.runtime.pinBoss) {
    const boss = ctx.state.boss;
    const pin = ctx.runtime.pinBoss;
    boss.speed = 0;
    boss.x = pin.x;
    boss.y = pin.y;
    boss.vx = 0;
    boss.vy = 0;
    boss.intentVx = 0;
    boss.intentVy = 0;
  }
  if (ctx.runtime.pinAlly && ctx.runtime.allyId != null) {
    pinUnitAt(
      ctx.state.units.find((u) => u.id === ctx.runtime.allyId),
      ctx.runtime.pinAlly,
    );
  }
  if (ctx.runtime.pinPrimary && ctx.runtime.primaryId != null) {
    pinUnitAt(
      ctx.state.units.find((u) => u.id === ctx.runtime.primaryId),
      ctx.runtime.pinPrimary,
    );
  }
  collectTickMetrics(ctx, dt);
  const done =
    ctx.state.time >= ctx.scenario.durationSec || ctx.state.phase !== "playing";
  if (done) ctx.finished = true;
  return ctx.finished;
}

export type FinishSimRunOpts = {
  clearSave?: boolean;
  clearMap?: boolean;
};

export function finishSimRun(
  ctx: SimRunContext,
  opts: FinishSimRunOpts = {},
): SimReport {
  ctx.finished = true;
  const clearSave = opts.clearSave !== false;
  const clearMap = opts.clearMap !== false;
  try {
    return {
      scenarioId: ctx.scenarioId,
      patch: ctx.patch,
      metrics: finalizeMetrics(ctx.scenario, ctx.focus, ctx.state, ctx.acc),
      focusDefId: ctx.scenario.focusDefId,
    };
  } finally {
    if (clearSave) setRaidAiSave({});
    if (clearMap) clearSimMapOverride();
  }
}

export function runScenario(
  id: SimScenarioId,
  patch: SimEditPatch = {},
): SimReport {
  try {
    // Headless / vitest: lock non-focusPrimaries by default so single-axis
    // reads stay pure. Pass lockNonFocus:false to exercise multi-axis (e.g.
    // measure spacing without locking other axes).
    const ctx = prepareSimRun(id, { lockNonFocus: true, ...patch });
    runSimSteps(ctx, DT, stepSimRun, {
      maxSec: Math.max(ctx.scenario.durationSec + 30, 120),
    });
    return finishSimRun(ctx);
  } catch (err) {
    setRaidAiSave({});
    clearSimMapOverride();
    throw err;
  }
}
